import * as React from 'react'
import './App.css'
import HeaderClock from './components/HeaderClock'
import ExposureChart from './containers/ExposureChart'
import LoginForm from './containers/LoginForm'
import ManageAlerts from './containers/ManageAlerts'
import RedButtons from './components/VirtualServerControl/VirtualServerControlPage'
import Settings from './containers/Settings'
import Toasts from './containers/Toasts'
import * as result from './result'
import { Result } from './result'
import { AlertLevel, AlertMessage, AutomaticRedButtonEnabled, ClientExposuresTransactions, getPageUrl, Hubs, AlertLevelsDict, Pages, Permission, RJEConnectionStatus, User, } from './types/types'
import { customAlertLevelBadge, getConnectionInfo, getNewLevel, getOneAlertPerClientAndMetric, userCountsAsLoggedIn, } from './utils'
import { BrowserRouter, NavLink, Route, Routes } from 'react-router-dom'
import { ClientCode, ClientConfig } from "./types/clients";
import ManageClientsForm from "./components/ManageClients/ManageClientsForm";
import { getActiveClients, getAppVersion } from "./web/clients";
import { SuspendedVirtualServersList } from "./components/SuspendedVirtualServersList";
import RevExposureChart from './containers/RealisticExposureChart';

export interface Props {
  readonly alerts: ReadonlyArray<AlertMessage>
  readonly alertLevel: AlertLevel
  readonly automaticRedButtonEnabled: AutomaticRedButtonEnabled
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
  readonly dataLastUpdated: Date | undefined
  readonly exchangeFreshnessText: string
  readonly freshnessText: string
  readonly hasPermission: {
    (permission: Permission): (user: User) => boolean
    (permission: Permission, user: User): boolean
  }
  readonly hubs: Hubs
  readonly initialised: boolean
  readonly name: string
  readonly metricLevels: AlertLevelsDict
  readonly localTime: Date
  readonly currentPage: Pages
  readonly onAlertBadgeClick: (level: AlertLevel) => () => void
  readonly selectedClientCode: ClientCode | undefined
  readonly user: User
  readonly totalMessagesCount: number
  readonly dailyTotalNewOrdersValue: number
  readonly dailyExecutedOrdersValue: number
  readonly setClientSettings: (clientSettings: ReadonlyArray<ClientConfig>) => void
}
interface State {
  appVersion: string;
}

class App extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
      this.state = {
        appVersion: '',
      };
  }
  async componentDidMount(): Promise<void> {
    const appVersion = await getAppVersion();
    this.setState({ appVersion: appVersion });
    if (userCountsAsLoggedIn(this.props.user)) {
      this.loadClientSettings();
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (
      !userCountsAsLoggedIn(prevProps.user) &&
      userCountsAsLoggedIn(this.props.user)
    ) {
      this.loadClientSettings();
    }
  }

  private async loadClientSettings(): Promise<void> {
    try {
      const clientSettings = await getActiveClients();
      this.props.setClientSettings(clientSettings);
    } catch (error) {
      console.error('Failed to load client settings', error);
    }
  }

  public render(): JSX.Element {
    if (!this.props.initialised) {
      // This should only ever be hit before the first Reduce has happened.
      return <></>
    }

    const {
      alertLevel,
      alerts,
      automaticRedButtonEnabled,
      currentPage,
      exchangeFreshnessText,
      freshnessText,
      hasPermission,
      hubs,
      localTime,
      onAlertBadgeClick,
      selectedClientCode,
      totalMessagesCount,
      dailyTotalNewOrdersValue,
      dailyExecutedOrdersValue,
      user,
    } = this.props

    const sidebarItemPermissions: { [index in Pages]: Permission } = {
      'Alerts': 'canViewAlerts',
      'Main Chart': 'canViewMainChart',
      'Manage Clients': 'canViewClientDetails',
      'Red Buttons': 'canRedButton',
      'Suspensions': 'canViewAlerts',
      'Reports': 'canViewMainChart',
      'Settings': 'canViewTvModeAndSettings',
      'TV Mode': 'canViewTvModeAndSettings',
    };

    const sidebarItems = Object.keys(sidebarItemPermissions).filter(key => hasPermission(sidebarItemPermissions[key as Pages], user)) as ReadonlyArray<Pages>

    const rjeStatusResult: Result<RJEConnectionStatus, string> = result.map(
      data => data.rjeConnectionStatus,
      getConnectionInfo(hubs.clientData),
    )

    const rjeStatusText = userCountsAsLoggedIn(user)
      ? result.withDefault('Waiting for Gatekeeper...', rjeStatusResult)
      : 'Log in to see connection status.'

    const rjeStatusClass =
      'theme--base rje-connection-status rje-connection-status--' +
      result.withDefault('not-applicable', rjeStatusResult)

    return (
      <>
        <BrowserRouter>
          <Toasts />
          <header className='header theme--primary'>
            <div className='left__header'>
              <img src='/images/logo_white.svg' alt='ABN AMRO Logo' className='header__logo'/>
            </div>
            <a href='/' className='suppress-link-styles header__title'>
              Gatekeeper
              <div className='version_details'>version {this.state.appVersion}</div>
            </a>
            <div className={`header__status-info`}>
              <div className='header__status-info__texts'>
                <div>
                  <small>Daily Total New Order Value:</small>
                </div>
                <div className='text-center'>¥ {dailyTotalNewOrdersValue.toLocaleString()}</div>
                <div className='text-center'>{simpleMetrics(dailyTotalNewOrdersValue)}</div>
              </div>
            </div>
            <div className={`header__status-info`}>
              <div className='header__status-info__texts'>
                <div>
                  <small>Daily Executed Order Value:</small>
                </div>
                <div className='text-center'>¥ {dailyExecutedOrdersValue.toLocaleString()}</div>
                <div className='text-center'>{simpleMetrics(dailyExecutedOrdersValue)}</div>
              </div>
            </div>
            {user.type === 'LoggedIn' || user.type === 'FailedToLogout' ? (
              <div className={`header__status-info`}>
                <div className='header__status-info__texts'>
                  <div>
                    <small>Automatic Red Button Status:</small>
                  </div>
                  <div className='text-center'>{automaticRedButtonEnabled}</div>
                </div>
              </div>
            ) : null}

            <div className={`header__status-info ${rjeStatusClass}`}>
              <div className='header__status-info__texts'>
                <div>
                  <small>GK → RJE:</small>
                </div>
                <div>{rjeStatusText}</div>
              </div>
            </div>
            <div className='header__status-info'>
              <HeaderClock tzname='Asia/Tokyo' time={localTime}/>
            </div>
            <div className='header__status-info' style={{minWidth: '17em'}}>
              <div className='header__status-info__texts'>
                <div>{freshnessText}</div>
                <div>
                  {totalMessagesCount.toLocaleString()}
                  <small> exchange messages processed</small>
                </div>
                <div>
                  <small>{exchangeFreshnessText}</small>
                </div>
              </div>
            </div>
          </header>
          <div className='content-and-sidebar__container'>
            <div className='sidebar theme--secondary'>
              {sidebarItems.map((name: Pages) => {
                const display: JSX.Element | string =
                  name !== 'Alerts'
                    ? name
                    : showAlertsCount(onAlertBadgeClick, alerts)
                return sidebarItem(
                  name,
                  display,
                )
              })}
              <div className='sidebar-version'>
              </div>
            </div>
            <div className='content theme--black-on-white'>
              {renderCurrentPage(user, selectedClientCode)}
            </div>
          </div>
          <div className={'alert-level__overlay alert-level--' + alertLevel} />
        </BrowserRouter>
      </>
    )
  }
}

function showAlertsCount(
  onBadgeClick: Props['onAlertBadgeClick'],
  alerts: ReadonlyArray<AlertMessage>,
): JSX.Element | string {
  if (alerts.length === 0) {
    return 'Alerts'
  }

  // NB: the meaning of these changes if the alerts aren't passed from the
  // server in newest-first order.
  const levels: ReadonlyArray<AlertLevel> = getOneAlertPerClientAndMetric(
    alerts,
  ).map((alert) =>
    getNewLevel(alert.alertSeverity, alert.exceeded, alert.limitType),
  )

  const breaches: number = levels.filter(level => level === 'Breach').length
  const warnings: number = levels.filter(level => level === 'Warn').length
  const notifies: number = levels.filter(level => level === 'Notify').length

  return (
    <>
      <span>Alerts</span>
      {customAlertLevelBadge(
        'theme--black-on-white',
        'Breach',
        breaches.toString(),
        onBadgeClick,
      )}
      {customAlertLevelBadge(
        'theme--black-on-white',
        'Warn',
        warnings.toString(),
        onBadgeClick,
      )}
      {customAlertLevelBadge(
        'theme--black-on-white',
        'Notify',
        notifies.toString(),
        onBadgeClick,
      )}
    </>
  )
}

function sidebarItem(
  name: Pages,
  display: JSX.Element | string,
): JSX.Element {
  return (
    <NavLink
      className={({ isActive }) => "sidebar__item suppress-link-styles" + (isActive ? " sidebar__item--active" : "")}
      to={getPageUrl(name)}
      key={name}
    >
      {display}
    </NavLink>
  )
}

function renderCurrentPage(
  user: User,
  selectedClient: ClientCode | undefined,
): JSX.Element {
  if (!userCountsAsLoggedIn(user)) {
    return (
      <>
        <h1 className='no-margin-top'>Login</h1>
        <LoginForm />
      </>
    )
  }

  return <Routes>
    <Route path="/" element={
      <>
        <div className="scatter-plot-group" style={{ display: 'flex', width: '100%' }}>
          <RevExposureChart tvMode={false} />
          <ExposureChart tvMode={false} />
        </div>

      </>
    } />
    <Route path="/alerts" element={<ManageAlerts />} />
    <Route path="/manage-clients" element={
      <>
        <h1 className='no-margin-top'>{selectedClient === undefined
          ? 'Manage Clients'
          : `Manage Client: ${selectedClient}`}</h1>
        <ManageClientsForm />
      </>
    } />
    <Route path="/red-buttons/:virtualServerId?" element={<RedButtons />} />
    <Route path="/suspensions" element={<SuspendedVirtualServersList />} />
    <Route path="/tv-mode" element={
      <>
        <div style={{ display: 'flex', width: '100%' }}>
          <h1 className='no-margin-top'>TV Mode</h1>
          <RevExposureChart tvMode={true} />
          <ExposureChart tvMode={true} />
        </div>
      </>
    } />
    <Route path="/settings" element={
      <>
        <h1 className='no-margin-top'>Settings</h1>
        <Settings />
      </>
    } />
    <Route path="/reports" element={
      <>
        <h1 className='no-margin-top'>Evidence Log</h1>
        <a href='/api/Reports/EvidenceLog'>Download Evidence Log</a>
        <br />
        <a href='/api/Reports/SimpleEvidenceLog'>Download Simple Evidence Log</a>
        <br />
        <br />
        <h1 className='no-margin-top'>Virtual Server Numbers in Feed</h1>
        <a href='/api/Reports/VirtualServersInFeed'>Download Report</a>
      </>
    } />
    <Route path='*' element={<h1>Not found</h1>} />
  </Routes>
}

function simpleMetrics(value: number) {

  if (value >= 1e33) {
    return '(decillion)';
  } else if (value >= 1e30) {
    return '(nonillion)';
  } else if (value >= 1e27) {
    return '(octillion)';
  } else if (value >= 1e24) {
    return '(septillion)';
  } else if (value >= 1e21) {
    return '(quadrillion)';
  }else if (value >= 1e18) {
    return '(quintillion)';
  }else if (value >= 1e15) {
    return '(quadrillion)';
  } else if (value >= 1e12) {
    return '(trillion)';
  } else if (value >= 1e9) {
    return '(billion)';
  } else if (value >= 1e6) {
    return '(million)';
  }
}

export default App
